import { useState } from "react";
import { format, parseISO } from "date-fns";
import { fr, enUS, es, pt, arSA } from "date-fns/locale";

const DF = { fr, en: enUS, es, pt, ar: arSA };
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { usePrefs } from "@/lib/context";

const emit = (onChange, value) => onChange({ target: { value } });

// Drop-in replacement for <Input type="date">: value "YYYY-MM-DD", onChange receives {target:{value}}
export function DateInput({ value, onChange, className = "", ...rest }) {
  const { lang, t } = usePrefs();
  const [open, setOpen] = useState(false);
  const date = value ? parseISO(value) : undefined;
  const locale = DF[lang] || fr;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={rest["data-testid"]} className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 text-start text-sm shadow-sm transition-colors hover:border-slate-400 ${className}`}>
          <span className={date ? "text-slate-900" : "text-slate-400"}>{date ? format(date, "dd/MM/yyyy", { locale }) : t("datePh")}</span>
          <CalendarDays size={14} className="text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" locale={locale} formatters={{ formatWeekdayName: (d) => format(d, lang === "ar" ? "EEEEE" : "EEEEEE", { locale }) }} selected={date} defaultMonth={date} weekStartsOn={1}
          onSelect={(d) => { if (d) { emit(onChange, format(d, "yyyy-MM-dd")); setOpen(false); } }} />
      </PopoverContent>
    </Popover>
  );
}

// Replacement for datetime-local: value "YYYY-MM-DDTHH:mm"
export function DateTimeInput({ value, onChange, ...rest }) {
  const [d, tm] = (value || "").split("T");
  const testId = rest["data-testid"];
  return (
    <div className="grid grid-cols-[1fr_110px] gap-2">
      <DateInput value={d} onChange={(e) => emit(onChange, `${e.target.value}T${tm || "09:00"}`)} data-testid={testId} />
      <Input type="time" value={tm || ""} onChange={(e) => emit(onChange, `${d || format(new Date(), "yyyy-MM-dd")}T${e.target.value}`)} data-testid={`${testId}-time`} />
    </div>
  );
}
